import { ApplicationUuid, ExclusionWindow } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import moment from 'moment-timezone';
import { escapeCSV } from '@bringg-frontend/utils';

import RootStore from 'bringg-web/stores/root-store';
import { timezoneProvider } from 'bringg-web';

type ExclusionWindowsExport = {
	id: number;
	name: string;
	external_team_id: string;
	start_time: string;
	end_time: string;
	service_area_external_ids?: string;
	service_plan_external_id?: string;
	tasks_limit?: number;
	repeat?: string;
	exclusion_type?: number;
};

const CSV_CHECK_MARK = 'X';

function exclusionWindowsExportToCSVContent(array: ExclusionWindowsExport[]): string {
	if (!array.length) {
		return '';
	}

	const headers = Object.keys(array[0]).join(',');
	const rows = array.map(item => {
		return Object.values(item).map(escapeCSV).join(',');
	});
	return [headers, ...rows].join('\n');
}

function exclusionWindowsToExport(
	exclusionWindows: ExclusionWindow[],
	dateTimeFormat: string
): ExclusionWindowsExport[] {
	const { teamsStore, serviceArea, servicePlansStore, applicationStore } = getRoot<RootStore>().data;

	const hasTaskLimitInstalled = applicationStore.isApplicationInstalled(ApplicationUuid.ExclusionWindowTasksLimitApp);

	return exclusionWindows.map(exclusionWindow => {
		const team = teamsStore.get(exclusionWindow.team_id);
		const timezone = timezoneProvider.getTimezoneByTeamId(exclusionWindow.team_id);
		const serviceAreasExternalIds = exclusionWindow.service_area_ids.map(id => serviceArea.get(id)?.external_id);
		const servicePlanExternalId = servicePlansStore.get(exclusionWindow.service_plan_id)?.external_id;

		return {
			id: exclusionWindow.id,
			name: exclusionWindow.name,
			external_team_id: team?.external_id,
			start_time: moment.tz(exclusionWindow.start_time, timezone).format(dateTimeFormat),
			end_time: moment.tz(exclusionWindow.end_time, timezone).format(dateTimeFormat),
			service_area_external_ids: serviceAreasExternalIds?.join(',') || '',
			service_plan_external_id: servicePlanExternalId || '',
			...(hasTaskLimitInstalled ? { tasks_limit: exclusionWindow.tasks_limit ?? undefined } : {}),
			repeat: exclusionWindow.repeat ? CSV_CHECK_MARK : '',
			exclusion_type: exclusionWindow.exclusion_type ?? undefined,
			delete: '' // keep for template
		};
	});
}

export const exclusionWindowsToCSV = (
	exclusionWindows: ExclusionWindow[],
	dateTimeFormat: string,
	fileName: string
) => {
	const exports = exclusionWindowsToExport(exclusionWindows, dateTimeFormat);
	const csvContent = exclusionWindowsExportToCSVContent(exports);

	const blob = new Blob([csvContent], { type: 'text/csv' });
	return new File([blob], fileName, { type: 'text/csv' });
};
